import React, {FC, useEffect} from 'react';
import {connect} from 'react-redux';

import {ResolveStateProps} from '../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {ContactDetailsPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../srcCommon/helpers/reselectHelpers';
import {CardModel} from '../../models/api/cardModel';
import {VisualContactModel} from '../../models/api/visualContactModel';
import {AppState} from '../../store/appState';
import {
  fetchAccountForAccountDetailsAction,
  noAccountForAccountDetailsAction,
} from '../../store/panels/accounts/accountDetailsPanelActions';
import {makeGetContactDetailsPanel} from '../../store/panels/contacts/contactDetailsPanelSelectors';
import {ContactDetailsPanelAccountDetailsSubState} from '../../store/panels/contacts/contactDetailsPanelSubState';
import {ContactDetailsAccountDetailsWrapper} from './contactDetailsAccountDetailsWrapper';

/*
 * Props.
 */

interface ContactDetailsAccountDetailsWrapperContainerPublicProps {
  panelId: ContactDetailsPanelId;
  card: CardModel | undefined;
  contact: VisualContactModel | undefined;
  subState: ContactDetailsPanelAccountDetailsSubState;
  isLoading: boolean;
}
type ContactDetailsAccountDetailsWrapperContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type ContactDetailsAccountDetailsWrapperContainerDispatchProps = ResolveDispatchProps<
  typeof mapDispatchToProps
>;

type ContactDetailsAccountDetailsWrapperContainerProps =
  ContactDetailsAccountDetailsWrapperContainerPublicProps &
    ContactDetailsAccountDetailsWrapperContainerDispatchProps &
    ContactDetailsAccountDetailsWrapperContainerStateProps;

/*
 * Selectors.
 */

function getCard(_state: AppState, props: ContactDetailsAccountDetailsWrapperContainerPublicProps) {
  return props.card;
}

function makeGetSelectedDomain(getContactDetailsPanel: ReturnType<typeof makeGetContactDetailsPanel>) {
  return createSelector([getContactDetailsPanel, getCard], (panel, card) =>
    card?.accountId ? undefined : panel?.selectedDomain,
  );
}

function makeGetPanelLoadingStatus(getContactDetailsPanel: ReturnType<typeof makeGetContactDetailsPanel>) {
  return createSelector([getContactDetailsPanel], (panel) => panel?.status);
}

/*
 * Component.
 */

const ContactDetailsAccountDetailsWrapperContainerComponent: FC<
  ContactDetailsAccountDetailsWrapperContainerProps
> = (props) => {
  const {
    panelId,
    subState,
    card,
    contact,
    isLoading,
    selectedDomain,
    panelLoadingStatus,
    fetchAccount,
    noAccountForAccountDetails,
  } = props;

  useEffect(() => {
    if (!isLoading && subState.isFetchRequired && subState.accountId) {
      fetchAccount(panelId, subState.accountId);
    }
    if (!isLoading && subState.isFetchRequired && !subState.accountId) {
      noAccountForAccountDetails(panelId);
    }
  }, [
    panelId,
    subState.isFetchRequired,
    subState.accountId,
    isLoading,
    fetchAccount,
    noAccountForAccountDetails,
  ]);

  return (
    <ContactDetailsAccountDetailsWrapper
      panelId={panelId}
      subState={subState}
      card={card}
      contact={contact}
      selectedDomain={selectedDomain}
      panelLoadingStatus={panelLoadingStatus}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ContactDetailsAccountDetailsWrapperContainerPublicProps>()(() => {
  const getContactDetailsPanel = makeGetContactDetailsPanel();
  return {
    selectedDomain: makeGetSelectedDomain(getContactDetailsPanel),
    panelLoadingStatus: makeGetPanelLoadingStatus(getContactDetailsPanel),
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  fetchAccount: fetchAccountForAccountDetailsAction,
  noAccountForAccountDetails: noAccountForAccountDetailsAction,
}));

export const ContactDetailsAccountDetailsWrapperContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContactDetailsAccountDetailsWrapperContainerComponent);
